import { func, required, validationContext } from 'validx'
import { action, computed, makeObservable, observable } from 'mobx'
import {
  ExtensionBillingInfoType,
  InventoryItemExtensionBillingType,
} from '../../../InventoryItem'
import JobProgressionBillingViewModel from './JobProgressionBillingViewModel'
import {
  AllowedProgressionTypes,
  AvailableProgressions,
  ExtensionBillingInfo,
  ExtensionProgressionBilling,
  ExtensionRegularBilling,
  getBillingType,
  progressionMap,
  ProgressionsNameMapping,
  ProgressionStepsNameMapping,
  SelectedProgression,
} from './declarations'

export class RegularEditorViewModel {
  /**
   * The client amount for the Inventory Item. This is the amount the client will
   * be charged.
   */
  clientAmount = 0

  /**
   * The pro payout amount for the Inventory Item. This is the amount the pro will
   * receive.
   */
  proPayoutAmount = 0

  validation = validationContext(this, {
    clientAmount: [
      required('This field is required.'),
      func(({ value }) =>
        this.isFlat
          ? value > 0 || 'The amount must be zero or greater.'
          : (value > 0 && value <= 100) ||
            'The percentage should be between 0 and 100',
      ),
    ],
    proPayoutAmount: [
      required('This field is required.'),
      func(({ value }) =>
        this.isFlat
          ? value > 0 || 'The amount must be zero or greater.'
          : (value > 0 && value <= 100) ||
            'The percentage should be between 0 and 100',
      ),
    ],
  })

  constructor(private billingType: getBillingType) {
    makeObservable(this, {
      isFlat: computed,
      clientAmount: observable,
      proPayoutAmount: observable,
      hydrate: action.bound,
      reset: action.bound,
    })
  }

  /**
   * Whether we are using a flat fee or percentage
   */
  get isFlat() {
    return this.billingType() === InventoryItemExtensionBillingType.FlatFee
  }

  /**
   * Whether the data is valid
   */
  validate(): boolean {
    return this.validation.reset().validate().isValid
  }

  /**
   * Restores the UI from saved data
   */
  hydrate(values: ExtensionBillingInfo): void {
    const { clientChargeAmount, proPayoutAmount } =
      values as ExtensionRegularBilling

    this.clientAmount = clientChargeAmount
    this.proPayoutAmount = proPayoutAmount
  }

  /**
   * Produces the values needed to submit to the api
   */
  toValues(): ExtensionRegularBilling {
    return {
      type: ExtensionBillingInfoType.Regular,
      clientChargeAmount: this.clientAmount,
      proPayoutAmount: this.proPayoutAmount,
    }
  }

  /**
   * Clears the data
   */
  reset() {
    this.clientAmount = 0
    this.proPayoutAmount = 0
    this.validation.reset()
  }
}

export class ProgressionEditorViewModel {
  /**
   * Map of selected progressions
   */
  selectedProgressions: progressionMap = new Map()

  /**
   * Modal to handle adding a new progression
   */
  modalVM = new JobProgressionBillingViewModel(this)

  constructor(public billingType: getBillingType) {
    makeObservable(this, {
      selectedProgressions: observable,
      setProgression: action.bound,
      removeProgression: action.bound,
      canAddProgression: computed,
      hydrate: action.bound,
      reset: action.bound,
    })
  }

  /**
   * Provides the available non selected progressions
   */
  get availableProgressions() {
    return AvailableProgressions.filter(
      (progression) => !this.selectedProgressions.has(progression.value),
    )
  }

  /**
   * Returns the selected progressions
   */
  get selectedProgressionsList() {
    return Array.from(this.selectedProgressions.values())
  }

  /**
   * Whether a new progression can be selected
   */
  get canAddProgression() {
    return this.selectedProgressions.size < AvailableProgressions.length
  }

  /**
   * Adds a new selected progression
   */
  setProgression(
    progression: AllowedProgressionTypes,
    selected: SelectedProgression,
  ) {
    this.selectedProgressions.set(progression, selected)
  }

  /**
   * Removes the progression from the selected map
   */
  removeProgression(progression: AllowedProgressionTypes) {
    this.selectedProgressions.delete(progression)
  }

  /**
   * Edits a selected progression
   */
  editProgression(progression: SelectedProgression) {
    this.modalVM.show(progression)
  }

  /**
   * Produces the values needed to submit to the api
   */
  toValues(): ExtensionProgressionBilling {
    return {
      type: ExtensionBillingInfoType.Progression,
      progressions: Array.from(this.selectedProgressions.values()).map(
        (progression) => ({
          progression: progression.value,
          steps: progression.steps.map((step) => {
            const { clientChargeAmount, proPayoutAmount } =
              step.pricingVM.toValues()

            return {
              step: step.value,
              clientChargeAmount,
              proPayoutAmount,
            }
          }),
        }),
      ),
    }
  }

  /**
   * Restores the UI from saved data
   */
  hydrate(values: ExtensionBillingInfo): void {
    const { progressions } = values as ExtensionProgressionBilling

    progressions.forEach((progression) => {
      const steps = progression.steps.map((step) => {
        const pricingVM = new RegularEditorViewModel(this.billingType)

        pricingVM.hydrate({
          clientChargeAmount: step.clientChargeAmount,
          proPayoutAmount: step.proPayoutAmount,
          type: ExtensionBillingInfoType.Regular,
        })

        return {
          name: ProgressionStepsNameMapping[step.step],
          value: step.step,
          pricingVM,
        }
      })

      this.selectedProgressions.set(progression.progression, {
        value: progression.progression,
        name: ProgressionsNameMapping[progression.progression],
        steps,
      })
    })
  }

  /**
   * Clears the selected progressions
   */
  reset(): void {
    this.selectedProgressions.clear()
  }
}
